import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Badge, ListGroup } from 'react-bootstrap';
import { Calendar, Views, View, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay, addHours } from 'date-fns';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './CronogramaGeral.css';
import { ptBR } from 'date-fns/locale/pt-BR';
import { CronogramaViewModel } from '../../viewmodel/CronogramaViewModel';
import { toast } from 'react-toastify';
import BackendErrorMessage from '../../components/BackendErrorMessage';
import { useBreadcrumbs } from '../../Routes/BreadcrumbContext';

const locales = { 'pt-BR': ptBR };

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// Tipos para o react-big-calendar
interface Event {
  id: string;
  title: string;
  start: Date;
  end: Date;
  allDay?: boolean;
  resource?: any;
}

interface Processo {
  id: string;
  nome: string;
  cor?: string;
}

interface Cronograma {
  id: string;
  atividades: Atividade[];
}

interface Atividade {
  id: string;
  hora: string;
  data: string;
  cronogramaId: string;
  unidade: string;
  descricao: string;
  lugar: string;
}

export interface ProcessoCronograma {
  processo: Processo;
  cronograma: Cronograma;
}



// Função para gerar cores distintas
const generateColorFromUUID = (uuid: string): string => {
  const hash = uuid.split('').reduce((acc, char) => {
    return char.charCodeAt(0) + ((acc << 5) - acc);
  }, 0);

  const hue = Math.abs(hash % 360);
  return `hsl(${hue}, 70%, 85%)`; // Tons mais claros para o fundo
};

export const CronogramaGeral: React.FC = ({

}) => {
  const [view, setView] = useState<View>(Views.MONTH);
  const [date, setDate] = useState(new Date());
  const [selectedProcessos, setSelectedProcessos] = useState<string[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [processosCronogramas, setProcessosCronogramas] = useState<ProcessoCronograma[]>([]);
  const { setBreadcrumbs } = useBreadcrumbs();
  useEffect(() => {
    setBreadcrumbs([
      { path: '/', breadcrumb: '🏠' },
      { path: '/processos', breadcrumb: 'Processos' },
      { path: `/processos/cronogramas/geral`, breadcrumb: 'Cronograma Geral' },
    ]);
  }, [setBreadcrumbs]);

  useEffect(() => {
    const fetchProcCronogramas = async () => {
      try {
        const cg = await CronogramaViewModel.getCronogramaGeral();
        setProcessosCronogramas(cg);
      }
      catch (error) {
        toast.error(BackendErrorMessage(error))
      }

    }
    fetchProcCronogramas();
  }, [])

  // Processos com cores geradas
  const processosComCores = useMemo(() => {
    return processosCronogramas.map(pc => {
      const hash = parseInt(pc.processo.id.substr(0, 8), 16) || 0;
      const hue = Math.abs(hash % 360);
      const borderColor = pc.processo.cor || `hsl(${hue}, 70%, 50%)`;

      return {
        ...pc,
        processo: {
          ...pc.processo,
          cor: pc.processo.cor || generateColorFromUUID(pc.processo.id),
          borderColor
        }
      };
    });
  }, [processosCronogramas]);

  // Converte atividades para eventos do calendário
  const eventos = useMemo(() => {
    return processosComCores.flatMap(pc =>
      pc.cronograma.atividades
        .filter(atividade =>
          selectedProcessos.length === 0 ||
          selectedProcessos.includes(pc.processo.id))
        .map(atividade => {
          const atividadeDate = parse(`${atividade.data} ${atividade.hora}`, 'yyyy-MM-dd HH:mm', new Date());
          const endDate = addHours(atividadeDate, 1);
          endDate.setHours(atividadeDate.getHours() + 1); // Assume 1 hora de duração

          return {
            id: atividade.id,
            title: `${atividade.descricao} (${pc.processo.nome})`,
            start: atividadeDate,
            end: endDate,
            allDay: false,
            resource: {
              ...atividade,
              processo: pc.processo
            }
          };
        })
    );
  }, [processosComCores, selectedProcessos]);

  // Filtros por processo
  const toggleProcessoSelection = (processoId: string) => {
    setSelectedProcessos(prev =>
      prev.includes(processoId)
        ? prev.filter(id => id !== processoId)
        : [...prev, processoId]
    );
  };

  // Estilização customizada dos eventos
  const eventStyleGetter = (event: Event) => {
    const processo = event.resource.processo;
    return {
      style: {
        backgroundColor: processo.cor,
        border: `2px solid ${processo.borderColor}`,
        color: '#333',
        borderRadius: '4px',
        fontSize: '0.85rem',
        padding: '2px 5px'
      }
    };
  };

  // Manipulador de seleção de evento
  const handleSelectEvent = useCallback((event: Event) => {
    setSelectedEvent(event);
  }, []);

  // Formatação customizada da barra de ferramentas
  const CustomToolbar = (toolbar: any) => {
    const goToBack = () => {
      toolbar.onNavigate('PREV');
    };

    const goToNext = () => {
      toolbar.onNavigate('NEXT');
    };

    const goToCurrent = () => {
      toolbar.onNavigate('TODAY');
    };

    const changeView = (newView: View) => {
      toolbar.onView(newView);
    };

    return (
      <div className="rbc-toolbar">
        <div className="rbc-btn-group">
          <button type="button" onClick={goToBack}>{'<'}</button>
          <button type="button" onClick={goToCurrent}>Hoje</button>
          <button type="button" onClick={goToNext}>{'>'}</button>
        </div>
        <span className="rbc-toolbar-label">
          {toolbar.label}
        </span>
        <div className="rbc-btn-group">
          <button
            type="button"
            className={view === Views.MONTH ? 'rbc-active' : ''}
            onClick={() => changeView(Views.MONTH)}
          >
            Mês
          </button>
          <button
            type="button"
            className={view === Views.WEEK ? 'rbc-active' : ''}
            onClick={() => changeView(Views.WEEK)}
          >
            Semana
          </button>
          <button
            type="button"
            className={view === Views.DAY ? 'rbc-active' : ''}
            onClick={() => changeView(Views.DAY)}
          >
            Dia
          </button>
          <button
            type="button"
            className={view === Views.AGENDA ? 'rbc-active' : ''}
            onClick={() => changeView(Views.AGENDA)}
          >
            Agenda
          </button>
        </div>
      </div>
    );
  };

  // Função para formatar datas em português
  const formatDate = (date: Date, formatStr: string): string => {
    return format(date, formatStr, { locale: ptBR });
  };

  return (
    <Container fluid className="mt-4">
      <Row>
        <Col lg={9}>
          <Card className="mb-4">
            <Card.Body style={{ minHeight: '700px' }}>
              <Calendar
                localizer={localizer}
                events={eventos}
                startAccessor="start"
                endAccessor="end"
                style={{ height: '100%' }}
                view={view}
                onView={setView}
                date={date}
                onNavigate={setDate}
                defaultView={Views.MONTH}
                eventPropGetter={eventStyleGetter}
                onSelectEvent={handleSelectEvent}
                components={{
                  toolbar: CustomToolbar
                }}
                messages={{
                  today: 'Hoje',
                  previous: 'Anterior',
                  next: 'Próximo',
                  month: 'Mês',
                  week: 'Semana',
                  day: 'Dia',
                  agenda: 'Agenda',
                  date: 'Data',
                  time: 'Hora',
                  event: 'Evento',
                  noEventsInRange: 'Nenhuma atividade neste período.',
                  showMore: (total) => `+${total} mais`,
                  allDay: 'Dia inteiro',
                }}
                culture="pt-BR"
                formats={{
                  dayFormat: (date: Date, culture: string | undefined, localizer: any) => localizer.format(date, 'EEEE, d', culture),
                  weekdayFormat: (date: Date, culture: string | undefined, localizer: any) => localizer.format(date, 'EEEEEE', culture),
                  monthHeaderFormat: (date: Date, culture: string | undefined, localizer: any) => localizer.format(date, 'MMMM yyyy', culture),
                  dayHeaderFormat: (date: Date, culture: string | undefined, localizer: any) => localizer.format(date, "EEEE, d 'de' MMMM", culture),
                  dayRangeHeaderFormat: ({ start, end }: { start: Date; end: Date }, culture: string | undefined, localizer: any) =>
                    `${localizer.format(start, "d 'de' MMMM", culture)} - ${localizer.format(end, "d 'de' MMMM yyyy", culture)}`
                }}
              />
            </Card.Body>
          </Card>

          {/* Filtros por Processo */}
          <Card className="mb-4">
            <Card.Header>
              <h5>Filtrar por Processo</h5>
            </Card.Header>
            <Card.Body>
              <div className="d-flex flex-wrap gap-2">
                {processosComCores.map(pc => (
                  <Button
                    key={pc.processo.id}
                    variant={selectedProcessos.includes(pc.processo.id) ? 'primary' : 'outline-primary'}
                    onClick={() => toggleProcessoSelection(pc.processo.id)}
                    style={!selectedProcessos.includes(pc.processo.id) ? {
                      borderColor: pc.processo.borderColor,
                      color: pc.processo.borderColor
                    } : {}}
                  >
                    {pc.processo.nome}
                  </Button>
                ))}
                {selectedProcessos.length > 0 && (
                  <Button
                    variant="link"
                    onClick={() => setSelectedProcessos([])}
                  >
                    Limpar filtros
                  </Button>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3}>
          <Card>
            <Card.Header>
              <h5>Detalhes da Atividade</h5>
            </Card.Header>
            <Card.Body>
              {selectedEvent ? (
                <ListGroup variant="flush">
                  <ListGroup.Item>
                    <div className="d-flex justify-content-between">
                      <strong>Processo:</strong>
                      <Badge
                        pill
                        style={{
                          backgroundColor: selectedEvent.resource.processo.borderColor,
                          color: 'white'
                        }}
                      >
                        {selectedEvent.resource.processo.nome}
                      </Badge>
                    </div>
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>Data:</strong> {formatDate(selectedEvent.start, 'PPPP')}
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>Hora:</strong> {formatDate(selectedEvent.start, 'HH:mm')} - {formatDate(selectedEvent.end, 'HH:mm')}
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>Descrição:</strong> {selectedEvent.resource.descricao}
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>Unidade:</strong> {selectedEvent.resource.unidade}
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <strong>Local:</strong> {selectedEvent.resource.lugar}
                  </ListGroup.Item>
                </ListGroup>
              ) : (
                <p className="text-muted">Selecione uma atividade para ver os detalhes</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};